import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import OptionsList from './OptionsList';
import styles from './index.module.scss';
import cx from 'classnames';
import { IOption, InputStates, ISingleAutocompleteDropdown } from './interfaces';
import {
  ArrowDownSolidIcon,
  ArrowUpSolidIcon,
  CloseIcon,
  SearchIcon
} from '@navi/web-ui/lib/icons';
import { useOutsideClick } from '@navi/web-ui/lib/hooks';
import { Typography } from '@navi/web-ui/lib/primitives';
import { Search } from '@cp/src/utils/search';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';

const SingleAutocompleteDropdown = forwardRef<HTMLInputElement, ISingleAutocompleteDropdown>(
  (
    {
      options,
      selectedOption,
      containerClasses,
      placeholder = 'Type here to search',
      error,
      disabled,
      sortOptions,
      hideCloseOption,
      showSearchIcon,
      enableFuzzySearch = false,
      fuzzySearchAdditionalKeys = [],
      customSortFunction,
      customOptionTemplate,
      onSearch,
      onSelectionChange,
      notFoundCopy,
      customOptionsContainer,
      clickstreamEvent,
      ...restInputProps
    },
    ref
  ) => {
    SingleAutocompleteDropdown.displayName = 'singleAutocompleteDropdown';
    const [searchText, setSearchText] = React.useState('');
    const inputRef = React.useRef<HTMLInputElement>(null);
    const autocompleteRef = React.useRef<HTMLInputElement>(null);
    const [inputState, setInputState] = React.useState<InputStates>(
      error ? InputStates.ERROR : InputStates.DEFAULT
    );
    const [showPicker, setShowPicker] = React.useState(false);
    const [showError, setShowError] = React.useState(error);

    const [optionsToDisplay, setOptionsToDisplay] = useState(options);

    useEffect(() => {
      if (selectedOption) {
        const currentSelectedOption = options.find(option => option.value === selectedOption);
        if (currentSelectedOption) {
          setSearchText(currentSelectedOption.label);
        }
      } else {
        setSearchText('');
      }
    }, [selectedOption]);

    useEffect(() => {
      setOptionsToDisplay(options);
      const currentSelectedOption = options.find(option => option.value === selectedOption);
      if (currentSelectedOption) {
        setSearchText(currentSelectedOption.label);
      }
    }, [options]);

    // If component is focused, set the input state to active
    React.useEffect(() => {
      if (inputState === InputStates.ACTIVE) {
        inputRef?.current?.focus();
      }
    }, [inputState]);
    // Expose the inputRef to the parent component
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    useOutsideClick(autocompleteRef, () => {
      if (showPicker) {
        togglePicker();
      }
      setOptionsToDisplay(options);
      if (selectedOption) {
        const currentSelectedOption = options.find(option => option.value === selectedOption);
        if (currentSelectedOption) {
          setSearchText(currentSelectedOption.label);
        }
      }
      setInputState(InputStates.DEFAULT);
    });

    const handleFilter = (searchText: string) => {
      if (!searchText?.length) {
        // If search text is empty, show all options
        setOptionsToDisplay(options);
        return;
      }
      if (!enableFuzzySearch) {
        const filteredOptions = options.filter(option =>
          option.label.toLowerCase().includes(searchText.toLowerCase())
        );
        setOptionsToDisplay(filteredOptions);
        return;
      }
      if (enableFuzzySearch) {
        // fuzzy search
        const fuzzySearchKeys = [
          'label',
          ...fuzzySearchAdditionalKeys.map(key => `additionalData.${key}`)
        ];
        const fuzzySearchedOptions =
          searchText.length > 0
            ? Search(searchText, options, { all: true, keys: fuzzySearchKeys })?.map(
                (item: Record<string, never>) => {
                  return item.obj;
                }
              )
            : options;
        setOptionsToDisplay(fuzzySearchedOptions);
      }
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const searchText = event.target.value;
      if (onSearch && typeof onSearch === 'function') {
        onSearch(searchText); // Callback to the consumer component to handle API call
      }
      setSearchText(searchText);

      if (!showPicker) {
        togglePicker();
      }

      if (inputState !== InputStates.ACTIVE) {
        setInputState(InputStates.ACTIVE);
      }

      handleFilter(searchText);
    };

    const handleOptionClick = (option: IOption) => {
      if (disabled) {
        return;
      }

      setShowPicker(false);
      setOptionsToDisplay(options);

      const { value, label } = option || {};

      if (value) {
        setSearchText(label);

        // Callback to the consumer component to handle selection change
        onSelectionChange(option);
      }
    };

    const togglePicker = () => {
      setShowPicker(!showPicker);
    };

    const isOptionSelected = (option: IOption) => option.value === selectedOption;

    const handleContainerClick = () => {
      if (disabled) {
        return;
      }
      if (clickstreamEvent) {
        addClickstreamEvent(clickstreamEvent);
      }
      if (!showPicker) {
        setInputState(InputStates.ACTIVE);
      } else {
        setInputState(InputStates.DEFAULT);
        if (onSearch && typeof onSearch === 'function') {
          onSearch('');
        }
        setOptionsToDisplay(options);
      }
      setShowError('');
      togglePicker();
    };

    const handleClearAll = (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
      if (disabled) {
        return;
      }
      event.stopPropagation();
      onSelectionChange(null);
      setShowError('');
      setSearchText('');
      setInputState(InputStates.DEFAULT);
    };

    const ArrowIcon = showPicker ? ArrowUpSolidIcon : ArrowDownSolidIcon;

    const sortedOptions = useMemo(() => {
      if (!sortOptions) {
        return optionsToDisplay;
      }
      return [...optionsToDisplay].sort((a, b) => {
        if (customSortFunction) {
          return customSortFunction(a, b);
        }
        if (a.label < b.label) {
          return -1;
        }
        if (a.label > b.label) {
          return 1;
        }
        return 0;
      });
    }, [optionsToDisplay]);

    return (
      <div className={cx(styles.container)} ref={autocompleteRef}>
        <div
          className={cx(
            styles.autocompleteDropdown,
            {
              [styles.disabled]: disabled
            },
            {
              [styles.active]: inputState === InputStates.ACTIVE,
              [styles.error]: inputState === InputStates.ERROR,
              [styles.default]: inputState === InputStates.DEFAULT
            },
            containerClasses
          )}
          onClick={handleContainerClick}
        >
          {showSearchIcon ? (
            <SearchIcon
              width={28}
              height={28}
              color={inputState === InputStates.ACTIVE ? 'var(--blue-base)' : 'var(--grayscale-3)'}
            />
          ) : null}
          <div className={styles.inputContainer}>
            <input
              ref={inputRef}
              placeholder={placeholder}
              type="text"
              value={searchText}
              onChange={handleInputChange}
              disabled={disabled}
              {...restInputProps}
            />
          </div>
          <div className={styles.iconsContainer}>
            {selectedOption && !disabled && !hideCloseOption ? (
              <CloseIcon className={styles.clearAll} onClick={handleClearAll} />
            ) : null}
            <ArrowIcon width={9} />
          </div>
        </div>
        {showError ? (
          <Typography variant="p4" className={styles.error}>
            {showError}
          </Typography>
        ) : null}
        {showPicker ? (
          <OptionsList
            notFoundCopy={notFoundCopy}
            options={sortedOptions}
            customOptionsContainer={customOptionsContainer}
            isOptionSelected={isOptionSelected}
            onOptionClick={handleOptionClick}
            optionTemplate={customOptionTemplate}
          />
        ) : null}
      </div>
    );
  }
);

export default SingleAutocompleteDropdown;
